import axios from "axios";
import { FHIR_METHODS } from "config/constants/adapter_constants";
import { getTenantFromToken } from "config/helpers/common_helper";
import { handleLogout, TokenValidate } from "config/helpers/logout_helper";
import { auth } from "firebase-setup";
import jwtDecode from "jwt-decode";
import _ from "lodash";
import { toast } from "react-toastify";
import {
  change_app_language,
  change_login,
  reset_state,
} from "redux/actions/AppActions";
import { set_is_logging_out } from "redux/actions/AuthActions";
import { reset_subscription } from "redux/actions/SubscriptionAction";
import { constants } from "redux/constants/redux-constants";
import { store } from "redux/store";

const generateInterceptors = (AxiosInstance) => {
  AxiosInstance.interceptors.request.use(
    async (config) => {
      const token = localStorage.getItem(`jwtToken`);
      if (token) {
        const jwtPayload = jwtDecode(token);
        config.headers["Authorization"] = `Bearer ${token}`;
        config.headers["x-enliv-tenant-id"] = jwtPayload?.firebase?.tenant;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  AxiosInstance.interceptors.response.use(
    (response) => {
      TokenValidate();
      return response;
    },

    async (error) => {
      const originalRequest = error?.config;
      console.log("originalRequest._retry", originalRequest._retry);
      if (error?.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;
        // if (error?.response?.data?.message === "Unauthorized") {
        //   isTokenToast && toast.error("Your session has been expired, please login again.");
        //   isTokenToast = false;
        //   handleLogout()
        //   return Promise.resolve();
        // }
        try {
          const user = auth?.currentUser;
          if (user) {
            const token = await user.getIdToken(true);
            localStorage.setItem("jwtToken", token);
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return AxiosInstance(originalRequest);
          }
        } catch (err) {
          if (err?.code === "auth/user-token-expired") {
            handleLogout();
            return Promise.resolve();
            // toast.error("Your session has been expired, please login again.")
            // store.dispatch(set_is_logging_out(true))
            // const { app } = store.getState()
            // setTimeout(() => {
            //   const language = app?.language;
            //   store.dispatch(change_login(constants?.USER_LOGOUT))
            //   store.dispatch(reset_state())
            //   store.dispatch(change_app_language(language || "en"))
            //   store.dispatch(reset_subscription())
            //   store.dispatch(set_is_logging_out(false))
            // }, 1000);
          }
          console.log(err);
        }
      }
      return Promise.reject(error);
    }
  );
};

const generateMetaInterceptors = (AxiosInstance) => {
  AxiosInstance.interceptors.request.use(
    async (config) => {
      const token = localStorage.getItem(`jwtToken`);
      if (token) {
        const jwtPayload = jwtDecode(token);
        config.headers["Authorization"] = `Bearer ${token}`;
        // config.headers["x-enliv-tenant-id"] = jwtPayload?.firebase?.tenant ;
        config.headers["x-hasura-role"] = `admin`;
        config.headers[
          "x-hasura-admin-secret"
        ] = `c042293c-7ae1-43c5-bda1-fc3519f5fac2`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );
};

const generateBridgeRequestInterceptors = (AxiosInstance) => {
  AxiosInstance.interceptors.request.use(async (config) => {
    const token = localStorage.getItem("jwtToken");
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  });
};

let HTTPRequest = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

generateInterceptors(HTTPRequest);

export const HTTPAidaBaseService = axios.create({
  baseURL: process.env.REACT_APP_AIDA_SERVICE_URL,
});

generateInterceptors(HTTPAidaBaseService);

let HTTPRequestUnauthenticated = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

export const HTTPBridgeService = axios.create({
  //temproray base Url till Proxy BE gets deployed
  baseURL: process.env.REACT_APP_EHR_PROXY_URL,
  headers: {
    "x-api-key": process.env.REACT_APP_EHR_PROXY_API_KEY,
    "Content-Type": "application/json",
  },
});

export const HTTPLocalService = axios.create({
  baseURL: "http://localhost:8000",
  headers: {
    "x-api-key": process.env.REACT_APP_EHR_PROXY_API_KEY,
    "Content-Type": "application/json",
  },
});

// generateBridgeRequestInterceptors(HTTPLocalService);
// generateBridgeRequestInterceptors(HTTPBridgeService);

generateInterceptors(HTTPBridgeService);

export const HTTPVimService = axios.create({
  baseURL: "https://vim-dev-service-152128589088.us-east4.run.app",
  headers: {
    "Content-Type": "application/json",
  },
});

export const HTTPVimLocalService = axios.create({
  baseURL: "http://localhost:12000",
  headers: {
    "Content-Type": "application/json",
  },
});

generateBridgeRequestInterceptors(HTTPVimService);
generateBridgeRequestInterceptors(HTTPVimLocalService);

const FHIRSearch = (module, params) => {
  return HTTPRequest.get(
    `fhir/tenants/${getTenantFromToken()}/search/${module}${
      params ? "?" + params : ""
    }`
  );
};

const FHIRSave = (action, data) => {
  if (action === "executeBundle") {
    return HTTPRequest?.post(
      `fhir/tenants/${getTenantFromToken()}/` + "executeBundle",
      data
    );
  }
  let methodIdx = FHIR_METHODS.findIndex((f) => f.TYPE === action);
  if (methodIdx < 0) {
    console.error(
      `API Call Failed --> Please check if ${action} is a valid action.`
    );
    return false;
  }
  return HTTPRequest?.[_.lowerCase(action)](
    `fhir/tenants/${getTenantFromToken()}/` + FHIR_METHODS[methodIdx].RESOURCE,
    data
  );
};

const FHIRDelete = (resourceType, resourceId) => {
  return HTTPRequest?.delete(
    `fhir/tenants/${getTenantFromToken()}/deleteResource?resourceType=${resourceType}&resourceId=${resourceId}`
  );
};

const NokiService = (action, url = "", data) => {
  return HTTPAidaBaseService?.[_.lowerCase(action)](url, data);
};

const HTTPMetadata = (action, module, params = ``, payload = {}, url = "") => {
  const token = localStorage.getItem(`jwtToken`);
  let jwtPayload = {};
  if (token) jwtPayload = jwtDecode(token);
  let apiURL = process.env.REACT_APP_API_URL;
  let MetaHTTPRequest = axios.create({
    baseURL: apiURL,
  });
  generateMetaInterceptors(MetaHTTPRequest);
  const prepareUrl = url
    ? url + `/tenant/${getTenantFromToken()}`
    : `metadata/v1/${jwtPayload?.firebase?.tenant}/${module}${
        params ? "?" + params : ""
      }`;
  return MetaHTTPRequest?.[_.lowerCase(action)](prepareUrl, payload);
};

export default HTTPRequest;
export {
  FHIRSearch,
  FHIRSave,
  FHIRDelete,
  NokiService,
  HTTPMetadata,
  HTTPRequestUnauthenticated,
};
